import React, { useRef, useState } from "react";
import { Button, Select, Avatar, Spin } from "antd";
import { LeftOutlined, RightOutlined, EditOutlined, MessageOutlined, CameraOutlined, CalendarOutlined, WechatOutlined } from "@ant-design/icons";
import { theme } from "antd";
import { Link, useNavigate } from "react-router-dom";
import EditableField from "./EditableField";
import QuickMessageModal from "./QuickMessageModal";
import { useTranslation } from "react-i18next";
import { CustomerDetailData } from "../../../api/ChannelCustomer";

interface Props {
    customer: CustomerDetailData | null;
    staffOptions: any[];
    telesaleOptions?: any[];
    canEditAssignment?: boolean;
    onUpdateField: (field: string, value: any) => Promise<void>;
    onUploadAvatar?: (file: File) => Promise<void>;
    /** Gọi điện cho khách qua softphone */
    onCall?: () => void;
    /** Mở form đặt lịch hẹn nhanh (không truyền → sang trang tạo lịch hẹn) */
    onBook?: () => void;
    /** Số điện thoại phụ của khách */
    extraPhones?: string[];
    onCallPhone?: (phone: string) => void;
}

export default function TopHeaderBlock({
    customer,
    staffOptions,
    telesaleOptions = [],
    canEditAssignment = true,
    onUpdateField,
    onUploadAvatar,
    onCall,
    onBook,
    extraPhones = [],
    onCallPhone,
}: Props) {
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const { t, i18n } = useTranslation();
    const [openQuickModal, setOpenQuickModal] = useState(false);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [updatingField, setUpdatingField] = useState<string | null>(null);

    const handleFieldChange = async (field: string, val: any) => {
        setUpdatingField(field);
        try { await onUpdateField(field, val); } finally { setUpdatingField(null); }
    };
    const fileInputRef = useRef<HTMLInputElement>(null);

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return "—";
        try {
            return new Date(dateStr).toLocaleDateString(i18n.language === 'vi' ? "vi-VN" : "en-US");
        } catch {
            return dateStr;
        }
    };

    const currentStaffId = (customer as any)?.staff_id || (customer as any)?.staffs?.[0]?.id || null;
    const currentStaffName =
        (customer as any)?.staff?.name ||
        (customer as any)?.staffs?.[0]?.name ||
        (customer as any)?.assigned_user?.name ||
        null;

    const normalizedStaffOptions = (() => {
        const flat = Array.isArray(staffOptions) ? staffOptions.flatMap((g: any) => g.options || [g]) : [];
        const hasCurrent = flat.some((opt: any) => Number(opt?.value) === Number(currentStaffId));
        if (!currentStaffId || hasCurrent || !currentStaffName) {
            return flat;
        }
        return [{ label: currentStaffName, value: currentStaffId }, ...flat];
    })();

    const currentTelesaleId = (customer as any)?.telesale_id || null;
    const currentTelesaleName = (customer as any)?.telesale?.name || null;
    const normalizedTelesaleOptions = (() => {
        const flat = Array.isArray(telesaleOptions) ? telesaleOptions.flatMap((g: any) => g.options || [g]) : [];
        const hasCurrent = flat.some((opt: any) => Number(opt?.value) === Number(currentTelesaleId));
        if (!currentTelesaleId || hasCurrent || !currentTelesaleName) {
            return flat;
        }
        return [{ label: currentTelesaleName, value: currentTelesaleId }, ...flat];
    })();
    const firstConnection = customer?.connections?.[0];
    const channelConnectionId =
        firstConnection?.channel_customer?.channel_connection_id ??
        firstConnection?.channel_connection_id ??
        firstConnection?.channel_connection?.id ??
        null;
    const channelCustomersId = firstConnection?.channel_customers_id ?? null;

    const handleViewConversation = () => {
        if (channelConnectionId && channelCustomersId) {
            navigate(`/chat/${channelConnectionId}?customer_id=${channelCustomersId}`);
            return;
        }
        navigate('/chats');
    };

    return (
        <div className="space-y-3">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm">
                <Link to="/customer-demo" className="flex items-center gap-2">
                    <LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" />
                </Link>
                <Link to="/customer-demo" className="flex items-center gap-2 text-000000e0">
                    <span>Tổng đài</span>
                </Link>
                <span><RightOutlined className="w-4 h-4 text-[12px] text-gray-500 text-000000e0" /></span>
                <span className="text-000000e0">{customer?.display_name || t('cd.breadcrumb_detail')}</span>
            </div>

            {/* Yellow block */}
            <div
                style={{
                    padding: 16,
                    borderRadius: 8,
                }}
                className="
                    border
                    border-[#e5e7eb]
                    bg-[#fff]
                    flex
                    flex-col
                    gap-4
                    md:flex-row
                    md:items-center
                    md:justify-between
                    dark:bg-[#1f2937]
                "
            >
                {/* LEFT SIDE */}
                <div className="flex gap-4">
                    <div className="flex items-center gap-3">
                        <div
                            className="relative cursor-pointer group"
                            onClick={() => fileInputRef.current?.click()}
                            title="Thay đổi ảnh đại diện"
                        >
                            <Avatar
                                size={50}
                                src={customer?.avatar || customer?.avatar_url || undefined}
                                onError={() => {
                                    console.warn('Avatar load failed:', customer?.avatar || customer?.avatar_url);
                                    return true;
                                }}
                            >
                                {customer?.display_name?.charAt(0)?.toUpperCase() || "?"}
                            </Avatar>
                            <div className={`absolute inset-0 rounded-full bg-black bg-opacity-40 flex items-center justify-center transition-opacity ${avatarUploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                {avatarUploading ? (
                                    <Spin size="small" />
                                ) : (
                                    <CameraOutlined className="text-white text-lg" />
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (file && onUploadAvatar) {
                                        setAvatarUploading(true);
                                        try {
                                            await onUploadAvatar(file);
                                        } finally {
                                            setAvatarUploading(false);
                                        }
                                    }
                                    e.target.value = '';
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-1 text-000000e0">
                        <EditableField
                            value={customer?.display_name || ""}
                            onChange={(val) => onUpdateField("display_name", val)}
                            onCall={onCall}
                        />
                        <div className="text-sm text-gray-500 text-000000e0 flex flex-wrap items-center gap-3">
                            <span>{t('cd.created_at')} <strong>{formatDate(customer?.created_at)}</strong></span>
                            {extraPhones.length > 0 && (
                                <>
                                    <span className="h-[20px] w-[1px] bg-gray-300"></span>
                                    <span className="flex items-center gap-1.5 flex-wrap">Số phụ:
                                        {extraPhones.map(p => (
                                            <button key={p} type="button" onClick={() => onCallPhone?.(p)} title="Gọi số này"
                                                className="px-2 py-0.5 rounded border border-green-200 bg-green-50 text-green-700 text-xs font-medium hover:bg-green-100 cursor-pointer">{p}</button>
                                        ))}
                                    </span>
                                </>
                            )}
                            <span className="h-[20px] w-[1px] bg-gray-300"></span>

                            <div className="flex items-center gap-2">
                                <span>Nhân viên trực page</span>
                                <Select
                                    size="small"
                                    className="w-full sm:w-40"
                                    placeholder={t('cd.select_staff')}
                                    value={currentStaffId}
                                    options={normalizedStaffOptions}
                                    disabled={!canEditAssignment}
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    loading={updatingField === 'staff_id'}
                                    onChange={(val) => handleFieldChange("staff_id", val)}
                                />
                            </div>
                            <span className="h-[20px] w-[1px] bg-gray-300"></span>
                            <div className="flex items-center gap-2">
                                <span>Nhân viên Telesale</span>
                                <Select
                                    size="small"
                                    className="w-full sm:w-40"
                                    placeholder="Chọn Telesale"
                                    value={currentTelesaleId}
                                    options={normalizedTelesaleOptions}
                                    disabled={!canEditAssignment}
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    loading={updatingField === 'telesale_id'}
                                    onChange={(val) => handleFieldChange("telesale_id", val)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT SIDE BUTTONS */}
                <div className="
                    flex
                    flex-col
                    gap-2
                    w-full
                    sm:w-auto
                    sm:flex-row
                "
                >
                    <Button
                        icon={<MessageOutlined />}
                        className="w-full sm:w-auto d-none"
                        onClick={() => setOpenQuickModal(true)}
                    >
                        {t('cd.quick_message')}
                    </Button>

                    <Button
                        icon={<CalendarOutlined />}
                        className="w-full sm:w-auto"
                        onClick={() => (onBook ? onBook() : navigate(`/appointment/create?phone=${encodeURIComponent(customer?.phone || "")}&name=${encodeURIComponent(customer?.display_name || "")}`))}
                    >
                        Tạo lịch hẹn
                    </Button>

                    <Button
                        icon={<WechatOutlined />}
                        className="w-full sm:w-auto bg-black text-white hover:!bg-gray-800"
                        onClick={handleViewConversation}
                    >
                        {t('cd.view_conversation')}
                    </Button>
                </div>

                <QuickMessageModal
                    open={openQuickModal}
                    onClose={() => setOpenQuickModal(false)}
                />
            </div>
        </div>
    );
}